import React from "react";
import { Link } from "react-router-dom";
import { Droplets, Shield } from "lucide-react";

/**
 * Public Information Security Policy. Lives at /security-policy so the
 * owner can paste the URL into Plaid's production questionnaire (and any
 * other vendor onboarding form). Mirrors the canonical .md file kept in
 * /app/memory/INFORMATION_SECURITY_POLICY.md so the two stay in sync.
 */
export default function SecurityPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold tracking-tight">
            <Droplets className="w-5 h-5 text-primary" /> Catskills Pool Supplies
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="security-login-link">Sign in</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6 leading-relaxed" data-testid="security-policy-page">
        <div className="flex items-start gap-3">
          <Shield className="w-7 h-7 text-primary mt-2 shrink-0" />
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Information Security Policy</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">How we keep your data safe</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Catskills Pool Supplies LLC · Effective February 2026 · Version 1.0 · Security contact:{" "}
              <a href="mailto:catskillspool@gmail.com" className="text-primary underline">catskillspool@gmail.com</a>
            </p>
          </div>
        </div>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">1. Purpose &amp; Scope</h2>
          <p>
            This policy describes how Catskills Pool Supplies LLC (the &quot;Company&quot;) protects the confidentiality, integrity, and availability of the
            information processed by its delivery-management and bookkeeping application (the &quot;Application&quot;).
          </p>
          <p>
            The Application is operated <strong>by the owner, for the owner</strong>, with limited delegated access for the Company&apos;s secretary and drivers.
            It is not sold to third parties and does not provide accounts to external organizations.
          </p>
          <p>This policy applies to the owner, the Company&apos;s secretary and driver accounts, every third-party service the Application integrates with, and all devices used to access it.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">2. Roles &amp; Responsibilities</h2>
          <table className="w-full text-sm border border-border">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Role</th><th className="text-left px-3 py-2">Responsibility</th></tr>
            </thead>
            <tbody>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Owner / Administrator</td><td className="px-3 py-2">Sets policy. Holds the only production credential. Reviews access at least quarterly.</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Secretary</td><td className="px-3 py-2">Day-to-day order and customer management. Cannot view financial settings, manage drivers, or access Plaid data.</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Driver</td><td className="px-3 py-2">View-only access to assigned delivery tickets and pool entry instructions. Cannot view dollar amounts, customer lists, or financial data.</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Customer</td><td className="px-3 py-2">Self-service portal access to their own account, invoices, and payment methods only.</td></tr>
            </tbody>
          </table>
          <p className="text-sm text-muted-foreground">Role assignments are reviewed by the owner at least once per quarter.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">3. Access Control</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Role-based access control (RBAC).</strong> Every API endpoint requires a specific role; the four roles above are strictly partitioned.</li>
            <li><strong>Strong password policy.</strong> Minimum 8 characters. Passwords are hashed with bcrypt before storage. Clear-text passwords are never logged.</li>
            <li><strong>Account lockout.</strong> After 5 consecutive failed sign-in attempts, the password is disabled and the user must complete an email-based password reset.</li>
            <li><strong>Multi-factor authentication (MFA).</strong> Sign-in from a new device or browser requires a one-time code sent by email. Trusted devices are recorded with a per-device token, User-Agent, first-seen IP, and last-used IP.</li>
            <li><strong>Device revocation.</strong> The owner can revoke any individual trusted device, or every device for any user, at any time from the admin Users panel.</li>
            <li><strong>Session expiry.</strong> Authentication tokens expire automatically; revoked devices invalidate the corresponding session immediately on the next request.</li>
            <li><strong>Production data is read by only one person:</strong> the owner.</li>
          </ul>
          <p>The owner has 2-factor authentication enabled on every external administrative dashboard used to run the Application (Plaid, Cardknox, Resend, Cloudflare, hosting platform, and email).</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">4. Data Protection</h2>
          <div>
            <h3 className="font-semibold mt-1">4.1 Encryption</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>In transit:</strong> all traffic forced to HTTPS (TLS 1.2+) via Cloudflare. HTTP requests are auto-upgraded.</li>
              <li><strong>At rest:</strong> the managed database encrypts all stored data with AES-256.</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mt-1">4.2 Sensitive data we explicitly do NOT store</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Raw payment card numbers, CVVs, or expiration dates. Cardknox iFields tokenizes the card inside the customer&apos;s browser.</li>
              <li>End-user bank credentials. Plaid is the only bank integration; the user enters bank credentials on the bank&apos;s own site.</li>
              <li>Plain-text user passwords. Passwords are stored only as bcrypt hashes.</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mt-1">4.3 Plaid access tokens</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Stored in an admin-only database collection.</li>
              <li>Never sent to the frontend, never logged.</li>
              <li>Deleted within seconds of the owner clicking &quot;Disconnect&quot; on a card.</li>
            </ul>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">5. Infrastructure &amp; Network Security</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Hosted on a managed Kubernetes platform (Emergent) with automatic security patching, dependency scanning, and isolation between services.</li>
            <li>The backend is only reachable through Cloudflare; direct origin access is blocked.</li>
            <li>The database is only reachable from the backend service over TLS; no public ingress, no public IP.</li>
            <li>Backend dependencies are pinned and updated through the managed package manager. Security patches from the hosting platform are applied as they are released.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">6. Privacy &amp; Consent</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>A public Privacy Policy is published at <Link to="/privacy" className="text-primary underline">catskillspoolsupplies.com/privacy</Link>.</li>
            <li>The Privacy Policy lists every third-party service we share data with and exactly what data is shared.</li>
            <li>Customers consent to the Privacy Policy at sign-up. The owner consents to the Plaid integration by clicking &quot;Connect a card / bank&quot; in the admin Finance page (Plaid is owner-only and is never offered to customers).</li>
            <li>We do not sell, rent, or otherwise share personal data with anyone outside the listed third parties.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">7. Data Retention &amp; Deletion</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Active accounts:</strong> retained while the account is active.</li>
            <li><strong>Closed accounts:</strong> deletion on request honored within 30 days. Invoice and tax records are retained for 7 years to comply with US tax law.</li>
            <li><strong>Plaid data:</strong> disconnecting a Plaid Item via the Application immediately deletes the access token. Imported transaction records remain in the ledger; the owner can delete them individually or in batches. All deletions are recoverable from a Restore History audit log.</li>
            <li><strong>Audit logs:</strong> retained for 30 days.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">8. Vendor &amp; Third-Party Management</h2>
          <p>Every third-party service is selected on the basis of independent compliance evidence (SOC 2, PCI-DSS, or equivalent):</p>
          <table className="w-full text-sm border border-border">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Vendor</th><th className="text-left px-3 py-2">Purpose</th><th className="text-left px-3 py-2">Compliance</th></tr>
            </thead>
            <tbody>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Plaid</td><td className="px-3 py-2">Read-only bank/card transaction import (owner only)</td><td className="px-3 py-2">SOC 2 Type II, ISO 27001</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Cardknox</td><td className="px-3 py-2">Credit-card processing</td><td className="px-3 py-2">PCI-DSS Level 1</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Resend</td><td className="px-3 py-2">Transactional email</td><td className="px-3 py-2">SOC 2 Type II</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Twilio</td><td className="px-3 py-2">Driver / customer SMS</td><td className="px-3 py-2">SOC 2 Type II, ISO 27001</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Cloudflare</td><td className="px-3 py-2">DNS, TLS, DDoS protection, WAF</td><td className="px-3 py-2">SOC 2 Type II, ISO 27001, PCI-DSS</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Google (OAuth)</td><td className="px-3 py-2">Optional &quot;Sign in with Google&quot;</td><td className="px-3 py-2">SOC 2 Type II, ISO 27001</td></tr>
            </tbody>
          </table>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">9. Vulnerability Management</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>The hosting platform applies operating-system and runtime security patches automatically.</li>
            <li>Backend dependencies are reviewed for known vulnerabilities at every deployment via the package manager&apos;s security advisories.</li>
            <li>Frontend dependencies are reviewed at the same cadence; advisories from <span className="font-mono">yarn audit</span> are addressed promptly.</li>
            <li>A clear-text change log is kept by the deployment platform; the owner reviews recent deploys before approving production releases.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">10. Incident Response</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Contain.</strong> Revoke every Plaid Item from the Plaid dashboard. Rotate the Plaid <span className="font-mono">client_secret</span>. Rotate the JWT signing secret. Revoke all trusted devices on the affected account.</li>
            <li><strong>Notify.</strong> Email <a href="mailto:security@plaid.com" className="text-primary underline">security@plaid.com</a> within 24 hours if Plaid data is involved. Email any affected customer within 72 hours if customer data is involved.</li>
            <li><strong>Investigate.</strong> Pull the relevant request/audit logs from the deployment platform. Reconstruct the timeline.</li>
            <li><strong>Remediate.</strong> Apply the patch, force a password reset on the affected account(s), redeploy.</li>
            <li><strong>Document.</strong> Record the incident, root cause, and corrective action in this policy&apos;s appendix.</li>
          </ol>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">11. Backups &amp; Continuity</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>The managed database is automatically backed up by the hosting platform on a rolling schedule (daily snapshots, multi-day retention).</li>
            <li>Application code and configuration are versioned in a private Git repository so any corrupted deployment can be rolled back to a previous release within minutes.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">12. Policy Review</h2>
          <p>The owner reviews this policy at least once per year, and whenever the Application&apos;s architecture, vendors, or roles change materially.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">13. Acceptance</h2>
          <p>By operating the Application, the owner agrees to follow this policy.</p>
          <p className="text-sm text-muted-foreground">Signed: Eli Fink — Owner, Catskills Pool Supplies LLC</p>
        </section>

        <footer className="pt-8 border-t border-border text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-3">
          <span>© Catskills Pool Supplies LLC</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-foreground">Privacy policy</Link>
            <Link to="/" className="hover:text-foreground">Home</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
